import { useQuery } from '@tanstack/react-query';
import {
    getLatestOfferedReportDate,
    getLatestOfferedReports,
    getOfferedReportExportData,
    getOfferedReportSummary,
} from '../../services/offeredReport';
import { QueryKey } from '../../enums/QueryKey';
import { getStartAndEndOfAMonth } from '../../utils/dateUtils';
import type { Dayjs } from '@utils/dayjs';
import { DATE_FORMATS } from '@callmart/types';

export function getLatestOfferedReportsQueryKey(date: string | Date | Dayjs, offeredCluster: number) {
    const { dateFrom, dateTo } = getStartAndEndOfAMonth(date);
    return [
        QueryKey.LatestOfferedReports,
        {
            dateFrom: dateFrom.format(DATE_FORMATS.ISO_DATETIME),
            dateTo: dateTo.format(DATE_FORMATS.ISO_DATETIME),
            offeredCluster,
        },
    ];
}

export function useLatestOfferedReports({
    date,
    offeredCluster,
}: {
    date: string | Date | Dayjs;
    offeredCluster: number;
}) {
    const { dateFrom, dateTo } = getStartAndEndOfAMonth(date);
    const queryKey = getLatestOfferedReportsQueryKey(date, offeredCluster);

    const { data, ...rest } = useQuery({
        queryKey,
        queryFn: () =>
            getLatestOfferedReports({
                dateFrom: dateFrom.toISOString(),
                dateTo: dateTo.toISOString(),
                offeredCluster,
            }),
        enabled: !!date && !!offeredCluster,
    });

    return { data: data || [], ...rest };
}

export function useOfferedReportsSummary({
    date,
    includeHistory = false,
}: {
    date: string | Date | Dayjs;
    includeHistory?: boolean;
}) {
    const { dateFrom, dateTo } = getStartAndEndOfAMonth(date);

    const { data, refetch } = useQuery({
        queryKey: [
            QueryKey.OfferedReportsSummary,
            {
                dateFrom: dateFrom.format(DATE_FORMATS.ISO_DATETIME),
                dateTo: dateTo.format(DATE_FORMATS.ISO_DATETIME),
                includeHistory,
            },
        ],
        queryFn: () =>
            getOfferedReportSummary({
                dateFrom: dateFrom.toISOString(),
                dateTo: dateTo.toISOString(),
                includeHistory,
            }),
        enabled: !!date,
    });

    return { data, refetch };
}

export function useOfferedReportsExportData({
    date,
    fetchImmediately = false,
}: {
    date: string | Date | Dayjs;
    fetchImmediately?: boolean;
}) {
    const { dateFrom, dateTo } = getStartAndEndOfAMonth(date);

    const { data, refetch } = useQuery({
        queryKey: [
            QueryKey.OfferedReportsExport,
            {
                dateFrom: dateFrom.format(DATE_FORMATS.ISO_DATETIME),
                dateTo: dateTo.format(DATE_FORMATS.ISO_DATETIME),
            },
        ],
        queryFn: () =>
            getOfferedReportExportData({
                dateFrom: dateFrom.toISOString(),
                dateTo: dateTo.toISOString(),
            }),
        enabled: !!date && fetchImmediately,
    });

    return { data, refetch };
}

export function useLatestOfferedReportDate({
    workingUnitLocationId,
    date,
}: {
    workingUnitLocationId: number;
    date: Dayjs;
}) {
    const { dateFrom, dateTo } = getStartAndEndOfAMonth(date);

    const queryKey = [
        QueryKey.LatestOfferedReportDate,
        {
            dateFrom: dateFrom.format(DATE_FORMATS.ISO_DATETIME),
            dateTo: dateTo.format(DATE_FORMATS.ISO_DATETIME),
            workingUnitLocationId,
        },
    ];

    return useQuery({
        queryKey,
        queryFn: () =>
            getLatestOfferedReportDate({
                dateFrom: dateFrom.toISOString(),
                dateTo: dateTo.toISOString(),
                workingUnitLocationId,
            }),
        enabled: !!date && !!workingUnitLocationId,
        refetchOnWindowFocus: false,
    });
}
